import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { AppConfigService } from '../services/app-config.service';
import { ApiService } from './api.service';
import { Promotion } from '../models/promotion.model';
import {
  AddToCartConfirmationDTO,
  AddToCartWithAttributesPayload,
  Attributes,
  CartData,
  Item,
  ProductItem,
  ServicePlan,
} from '../models/cart-data.model';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { Klarna } from '../typings/klarna.model';
import { GtagService } from './gtag.service';
import { CartParserService } from '../services/cart-parser.service';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private vistekUrl: string;
  public get VistekUrl(): string {
    return this.vistekUrl;
  }

  private cartDataSubject = new BehaviorSubject<CartData>(null);
  private savedforLaterCartDataSubject = new BehaviorSubject<ProductItem[]>([]);
  private _isLoadingSubject = new BehaviorSubject<boolean>(false);
  
  public get SavedforLaterCartDataSubject(): BehaviorSubject<ProductItem[]> {
    return this.savedforLaterCartDataSubject;
  }
  public get CartData(): CartData {
    return (window as any).cartData;
  }

  private set CartData(cartData: CartData) {
    this.parser.initCartData(cartData);
    (window as any).cartData = cartData;

    this.ModelSubject.next(cartData);
  }

  public get SavedForLaterCartData(): ProductItem[] {
    return (window as any).savedForLaterCartData;
  }
  private set SavedForLaterCartData(value: ProductItem[]) {
    (window as any).savedForLaterCartData = value;
    this.savedforLaterCartDataSubject.next(value);
  }

  public get ModelSubject(): BehaviorSubject<CartData> {
    return this.cartDataSubject;
  }

  public get isLoadingSubject(): BehaviorSubject<boolean> {
    return this._isLoadingSubject;
  }

  promoFormData: Promotion = new Promotion();

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private apiService: ApiService,
    private gtagService: GtagService,
    private parser: CartParserService
  ) {
    this.vistekUrl = this.appConfigService.vistekUrl;
  }

  selectPickupLocation(locationCode: string): void {
    this.isLoadingSubject.next(true);

    this.http
      .post<CartData>(`${this.vistekUrl}/api/cart/shipping/pickup-location/${locationCode}`, {}, {
        headers: this.apiService.xsrfHeader,
      }).pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
      })).subscribe((res) => {
        this.CartData = res;
      });
  }

  public getShippingRatesFromPostalCode(postalCode: string): void {
    let url = `${this.vistekUrl}/api/cart/shipping/postal-code/${postalCode}`;

    this.isLoadingSubject.next(true);
    this.http.post<CartData>(url, {}, {
      headers: this.apiService.xsrfHeader,
    }).pipe(
      finalize(() => {
        this.isLoadingSubject.next(false);
    })).subscribe((res) => {
      this.CartData = res;
    });
  }

  public setShippingMethod(method: string): void {
    let url = `${this.vistekUrl}/api/cart/shipping/method/${method}`;

    this.isLoadingSubject.next(true);
    this.http.post<CartData>(url, {}, {
      headers: this.apiService.xsrfHeader,
    }).pipe(
      finalize(() => {
        this.isLoadingSubject.next(false);
    })).subscribe((res) => {
      this.CartData = res;
    });
  }

  public selectConsolidatedShippingRate(shippingCompanyId: string, shippingServiceId: string, requestInsurance: boolean): void {
    const payload = {
        shippingCompanyId,
        shippingServiceId,
        requestInsurance
    };

    this.isLoadingSubject.next(true);

    this.http.post<CartData>(`${this.vistekUrl}/api/cart/shipping/consolidated-rate/select`, payload, {
      headers: this.apiService.xsrfHeader,
    }).pipe(
      finalize(() => {
        this.isLoadingSubject.next(false);
    })).subscribe((res) => {
      this.CartData = res;
    });
  }


  public loadShoppingCart(): void {
    let cartData$: Observable<CartData>;
    if (this.CartData) {
      cartData$ = of(this.CartData);
    } else {
      cartData$ = this.http.get<CartData>(`${this.vistekUrl}/cart/cartInfo`);
    }

    cartData$.subscribe((res) => {
      if (res?.CartItems?.length > 0) {
        const flatList = ([] as Item[]).concat(...res.CartItems);
        this.gtagService.viewCart(flatList);
      }
      this.CartData = res;
    });
  }

  public reloadShoppingCart(updated: boolean): void {
    this.isLoadingSubject.next(true);
    this.http.get<CartData>(`${this.vistekUrl}/cart/cartInfo`, { params: { updated } }).subscribe((res) => {
      this.isLoadingSubject.next(false);
      this.CartData = res;
    });
  }

  public loadSavedForLaterCartInfo(): void {
    this.http.get<ProductItem[]>(`${this.vistekUrl}/cart/list/SaveForLater?pages=1`).subscribe((res) => {
      const result = res ?? []
      this.SavedForLaterCartData = result;
      this.savedforLaterCartDataSubject.next(result);
    });
  }

  public postCartPromotion(): Observable<any> {
    return this.http.post(`${this.vistekUrl}/cart/promotion`, this.promoFormData);
  }

  public removeOrderItem(item: Item): Subscription {
    this.isLoadingSubject.next(true);

    const targetUrl = `${this.vistekUrl}/cart/remove/ShoppingCart/${item.RecordID}?ajax=yes`;
    console.log(targetUrl);
    return this.http
      .post<any>(targetUrl, null, { headers: this.apiService.xsrfHeader })
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
        }),
        tap((res) => {
          this.gtagService.removeFromCart(item);
          this.CartData = res;
        })
      )
      .subscribe();
  }

  public removeBundle(bundleId: number, items: ProductItem[]): Subscription {
    this.isLoadingSubject.next(true);

    const targetUrl = `${this.vistekUrl}/cart/bundle/remove/ShoppingCart/${bundleId}?ajax=yes`;
    return this.http
      .post<any>(targetUrl, null, { headers: this.apiService.xsrfHeader })
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
        }),
        tap((res) => {
          this.gtagService.removeItemsFromCart(items);
          this.CartData = res;
        })
      )
      .subscribe();
  }

  public removeSavedForlaterItem(item: Item): Subscription {
    const targetUrl = `${this.vistekUrl}/cart/remove/SaveForLater/${item.RecordID}?ajax=yes`;
    console.log(targetUrl);
    return this.http
      .post<any>(targetUrl, null, { headers: this.apiService.xsrfHeader })
      .pipe(
        tap((res) => {
          console.log(res);
          this.SavedForLaterCartData = this.SavedForLaterCartData.filter(
            (n) => n.RecordID !== item.RecordID
          );
          this.SavedforLaterCartDataSubject.next(this.SavedForLaterCartData);
        })
      )
      .subscribe();
  }

  public addServicePlan(parentItemWebcode: string, serviceplan: ServicePlan): Observable<boolean> {
    let targetUrl = '/cart/serviceplans';
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(
        targetUrl,
        { itemWebCode: parentItemWebcode, planResourceCode: serviceplan.ResourceCode },
        { headers: this.apiService.xsrfHeader }
      )
      .pipe(
        map(() => {
          this.sendAddToCartEventToGoogle(serviceplan);
          this.isLoadingSubject.next(false);

          return true;
        }, () => {
          this.isLoadingSubject.next(false);

          return false;
        }),
      )
  }

  public saveItemForLater(item: Item): Subscription {
    this.isLoadingSubject.next(true);

    console.log(item);
    const targetUrl = `${this.vistekUrl}/cart/move?From=ShoppingCart&To=SaveForLater&recordID=${item.RecordID}`;
    console.log(targetUrl);
    return this.http
      .post<any>(targetUrl, null, { headers: this.apiService.xsrfHeader })
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
        }),
        tap((res) => {
          this.CartData = res;
          this.loadSavedForLaterCartInfo();

          this.gtagService.addToWishlist(item);
        })
      )
      .subscribe();
  }

  public moveSavedItemToCart(item: Item): Subscription {
    this.isLoadingSubject.next(true);

    const targetUrl = `${this.vistekUrl}/cart/move?From=SaveForLater&To=ShoppingCart&recordID=${item.RecordID}`;
    console.log(targetUrl);
    return this.http
      .post<any>(targetUrl, null, { headers: this.apiService.xsrfHeader })
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);

        }),
        tap((res) => {
          this.CartData = res;
          this.SavedForLaterCartData = this.SavedForLaterCartData.filter(
            (n) => n.RecordID !== item.RecordID
          );
          this.SavedforLaterCartDataSubject.next(this.SavedForLaterCartData);
          this.gtagService.addItemsToCart([item]);
        })
      )
      .subscribe();
  }

  public updateGiftCardData(recordId: string, attributes: Attributes): Observable<CartData> {
    let targetUrl = `${this.vistekUrl}/cart/update/gift-card/${recordId}`;
    this.isLoadingSubject.next(true);

    return this.http.post<CartData>(targetUrl, attributes, { headers: this.apiService.xsrfHeader })
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
        }),
        tap((res) => {
          this.CartData = res;
        })
      );
  }

  public addToCartWithAttributes(payload: AddToCartWithAttributesPayload, source: string = ''): Observable<Item> {
    let targetUrl = `${this.vistekUrl}/cart/add-with-attributes`;

    return this.http
      .post<Item>(targetUrl, payload, { headers: this.apiService.xsrfHeader })
      .pipe(
        map((addedItem: Item) => {
          if (!addedItem) {
            return null;
          }

          this.gtagService.addItemsToCart([ addedItem ], source);

          return addedItem;
        }, 
        () => { throw 'Failed to add item to cart' }
      ))
  }
  
  public changeItemQuantity(item: ProductItem): Subscription {
    this.isLoadingSubject.next(true);

    const targetUrl = `${this.vistekUrl}/cart/quantity/${item.RecordID}/${item.Quantity}`;
    console.log(targetUrl);
    return this.http
      .post<any>(targetUrl, null, { headers: this.apiService.xsrfHeader })
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
        }),
        tap((res) => {
          console.log(res);
          this.CartData = res;
        })
      )
      .subscribe();
  }
  
  public updatePromoCode(promoCode: string): Observable<CartData> {
    this.isLoadingSubject.next(true);

    return this.http
      .patch<CartData>(
        `${this.vistekUrl}/api/cart/promocode`,
        { Code: promoCode },
        { headers: this.apiService.xsrfHeader }
      )
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);

        }),
        tap((res) => {
          this.CartData = res;
        })
      );
  }

  klarnaExpress(callbackData: Klarna.ExpressButton.CallbackData): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(`${this.vistekUrl}/cart/checkout/mode/klarnaexpress`, callbackData, {
        headers: this.apiService.xsrfHeader
      })
      .pipe(
        catchError((err, caught) => {
          this.isLoadingSubject.next(false);
          return throwError(err);
        })
      );
  }

  restoreCheckoutSession(): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(
        `${this.vistekUrl}/cart/checkout/mode/normal`,
        {},
        {
          headers: this.apiService.xsrfHeader
        }
      )
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
        }),
        catchError((err, caught) => {
          return throwError(err);
        })
      );
  }

  public getAddToCartConfirmationDTO(webCode: string, inventorySource: string): Observable<AddToCartConfirmationDTO> {
      let cartItem = { 
        CartType: 'ShoppingCart', 
        InventorySource: inventorySource, 
        Webcode: webCode, 
        Quantity: 1 
      };

      return this.http.get<AddToCartConfirmationDTO>(`${this.vistekUrl}/cart/add-confirmation/${cartItem.Webcode}`);
  }

  private sendAddToCartEventToGoogle(serviceplan: ServicePlan) {
    this.gtagService.gtm('add_to_cart', {
      ecommerce: {
        value: serviceplan.Price,
        items: [
          {
            item_id: serviceplan.ResourceCode,
            item_name: serviceplan.Description,
            price: serviceplan.Price,
            quantity: 1
          }
        ]
      }
    });
  }
}
